
import React, { useMemo, useState } from 'react';
import ProjectCard from './ProjectCard';
import CreateProjectDialog from './CreateProjectDialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, Plus, FolderOpen, UserSquare2, X } from 'lucide-react';
import { useProjects } from '@/hooks/useProjects';
import { useAuth } from '@/contexts/AuthContext';
import EmptyState from '@/components/common/EmptyState';

const TOP_CLIENTS_LIMIT = 5;

const ProjectList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const { user } = useAuth();
  const { projects, isLoading, error } = useProjects();

  // Top-N clienti per frequenza nei progetti.
  const topClients = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of projects) {
      const k = (p.client ?? '').trim();
      if (!k) continue;
      counts.set(k, (counts.get(k) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, TOP_CLIENTS_LIMIT);
  }, [projects]);

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          project.client.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;

    const matchesClient = !clientFilter || project.client === clientFilter;

    return matchesSearch && matchesStatus && matchesClient;
  });

  if (!user) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Effettua il login per visualizzare i tuoi progetti</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="py-12 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Caricamento progetti...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-12 text-center">
        <p className="text-destructive">Errore nel caricamento dei progetti</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 items-end justify-between">
        <div className="flex-1 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Cerca progetti..."
              className="pl-9"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="w-full sm:w-[180px]">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="active">Attivi</SelectItem>
                <SelectItem value="completed">Completati</SelectItem>
                <SelectItem value="archived">Archiviati</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <Button
          className="w-full sm:w-auto"
          onClick={() => setShowCreateDialog(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Nuovo Progetto
        </Button>
      </div>

      {/* Chip clienti top-5: shortcut per filtrare la lista per cliente più frequente */}
      {topClients.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
            <UserSquare2 className="h-3 w-3" />
            Clienti frequenti:
          </span>
          {topClients.map(([client, count]) => {
            const active = clientFilter === client;
            return (
              <button
                key={client}
                onClick={() => setClientFilter(active ? null : client)}
                className={
                  'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ' +
                  (active
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background hover:bg-muted border-border text-muted-foreground hover:text-foreground')
                }
                title={`Filtra progetti di ${client}`}
              >
                {client}
                <Badge variant={active ? 'secondary' : 'outline'} className="h-4 px-1 text-[10px] tabular-nums">
                  {count}
                </Badge>
              </button>
            );
          })}
          {clientFilter && (
            <button
              onClick={() => setClientFilter(null)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3" />
              Cancella
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProjects.map((project) => (
          <ProjectCard key={project.id} project={project} />
        ))}
        
        {filteredProjects.length === 0 && projects.length > 0 && (
          <div className="col-span-full">
            <EmptyState
              icon={<FolderOpen className="h-7 w-7" />}
              title="Nessun progetto trovato"
              description="Modifica la ricerca o cambia il filtro per stato per vedere i progetti."
            />
          </div>
        )}

        {projects.length === 0 && (
          <div className="col-span-full">
            <EmptyState
              icon={<FolderOpen className="h-7 w-7" />}
              title="Inizia da qui: crea il tuo primo progetto"
              description="Un progetto raccoglie tutti i preventivi di un cantiere. Da qui poi crei stratigrafie e generi i PDF da inviare al cliente."
              action={
                <Button onClick={() => setShowCreateDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Crea il tuo primo progetto
                </Button>
              }
              secondaryAction={<>Tip: collega prima un cliente da <em>Clienti</em> per avere i dati pre-compilati.</>}
            />
          </div>
        )}
      </div>

      <CreateProjectDialog 
        open={showCreateDialog} 
        onOpenChange={setShowCreateDialog} 
      />
    </div>
  );
};

export default ProjectList;
